/**
 * Dashboard Data Hook
 * 
 * Provides all data for the Credit Control Dashboard
 * Supports filtering by user/team
 */

import { useState, useEffect, useMemo } from 'react';

// Types
export interface TodaysPriorities {
  hasItems: boolean;
  collectionQueue: {
    count: number;
    amount: number;
  };
  unallocatedPayments: {
    count: number;
    amount: number;
  };
  promisesDueToday: {
    count: number;
    amount: number;
    brokenCount: number;
  };
  disputes: {
    count: number;
    amount: number;
  };
}

export interface AgingBand {
  label: string;
  amount: number;
  percentage: number;
  filterKey: string;
}

export interface AgingPortfolio {
  totalOutstanding: number;
  bands: AgingBand[];
}

export interface PerformanceSnapshot {
  todaysWins: {
    paymentsReceived: {
      count: number;
      amount: number;
    };
    promisesKept: number;
    casesResolved: number;
  };
  monthToDate: {
    collectionRate: {
      current: number;
      target: number;
      collectedAmount: number;
      totalDueAmount: number;
      pointsBelowTarget: number;
    };
    keyMetrics: {
      dso: {
        value: number;
        target: number;
      };
      promiseSuccess: {
        percentage: number;
        kept: number;
        total: number;
      };
      avgPaymentDays: {
        current: number;
        trend: {
          change: number;
          direction: 'up' | 'down';
        };
      };
    };
  };
}

export interface RiskAlert {
  id: string;
  type: 'strategy-inactive' | 'customer-risk';
  severity: 'urgent' | 'warning' | 'info';
  customerName?: string;
  customerId?: string;
  primaryIssue: string;
  context: string;
  clickUrl: string;
}

export interface CollectionStrategy {
  id: string;
  name: string;
  status: 'active' | 'paused';
  amountInCollection: number;
  lastRun?: Date;
  emailsSent?: number;
  impactedCustomers?: number;
  pausedBy?: string;
  pausedDate?: Date;
  pausedReason?: string;
}

export interface CollectionStrategies {
  totalInActiveCollection: number;
  strategies: CollectionStrategy[];
}

export interface AIInsights {
  confidence: number;
  recommendations: any[];
}

export interface FilterOption {
  id: string;
  name: string;
  initials: string;
}

export interface DashboardData {
  todaysPriorities: TodaysPriorities;
  agingPortfolio: AgingPortfolio;
  performanceSnapshot: PerformanceSnapshot;
  riskAlerts: RiskAlert[];
  collectionStrategies: CollectionStrategies;
  aiInsights: AIInsights;
  filterOptions: FilterOption[];
  refreshData: () => Promise<void>;
}

/**
 * Hook to fetch and manage dashboard data
 */
export function useDashboardData(selectedFilter: string = 'everyone'): DashboardData {
  const [isLoading, setIsLoading] = useState(false);

  // Mock data - replace with actual API calls
  const data = useMemo(() => {
    // Today's Priorities
    const todaysPriorities: TodaysPriorities = {
      hasItems: true,
      collectionQueue: {
        count: 19,
        amount: 465730,
      },
      unallocatedPayments: {
        count: 4,
        amount: 23250,
      },
      promisesDueToday: {
        count: 5,
        amount: 125686,
        brokenCount: 2,
      },
      disputes: {
        count: 4,
        amount: 33920,
      },
    };

    // Aging Portfolio
    const agingPortfolio: AgingPortfolio = {
      totalOutstanding: 2888000,
      bands: [
        {
          label: '0-90 days',
          amount: 2200000,
          percentage: 76,
          filterKey: '0-90',
        },
        {
          label: 'Over 90 days',
          amount: 688000,
          percentage: 24,
          filterKey: '90+',
        },
        {
          label: 'Over 180 days',
          amount: 318000,
          percentage: 11,
          filterKey: '180+',
        },
        {
          label: 'Over 365 days',
          amount: 98000,
          percentage: 3,
          filterKey: '365+',
        },
      ],
    };

    // Performance Snapshot
    const performanceSnapshot: PerformanceSnapshot = {
      todaysWins: {
        paymentsReceived: {
          count: 2,
          amount: 8200,
        },
        promisesKept: 5,
        casesResolved: 3,
      },
      monthToDate: {
        collectionRate: {
          current: 53,
          target: 85,
          collectedAmount: 2300000,
          totalDueAmount: 4300000,
          pointsBelowTarget: 32,
        },
        keyMetrics: {
          dso: {
            value: 42,
            target: 40,
          },
          promiseSuccess: {
            percentage: 53,
            kept: 21,
            total: 40,
          },
          avgPaymentDays: {
            current: 18,
            trend: {
              change: 3,
              direction: 'down',
            },
          },
        },
      },
    };

    // Risk Alerts
    const riskAlerts: RiskAlert[] = [
      {
        id: '1',
        type: 'strategy-inactive',
        severity: 'warning',
        primaryIssue: 'Post-Dispute Resolution paused',
        context: '45 customers affected • 0 emails sent',
        clickUrl: '#',
      },
      {
        id: '2',
        type: 'customer-risk',
        severity: 'urgent',
        customerName: 'Tech Solutions Inc',
        customerId: '2',
        primaryIssue: 'Invoice aging 90+ days - £89,000',
        context: 'Moved from 60-90 days this week',
        clickUrl: '/customers/2',
      },
      {
        id: '3',
        type: 'customer-risk',
        severity: 'urgent',
        customerName: 'Acme Corporation',
        customerId: '1',
        primaryIssue: 'Broken promise - £25,000',
        context: 'Payment was due 3 days ago',
        clickUrl: '/collections/promises?customer=1',
      },
      {
        id: '4',
        type: 'customer-risk',
        severity: 'warning',
        customerName: 'Global Industries Ltd',
        customerId: '3',
        primaryIssue: 'Credit score decreased to 67',
        context: 'Down from 82 last month',
        clickUrl: '/customers/3',
      },
    ];

    // Collection Strategies
    const collectionStrategies: CollectionStrategies = {
      totalInActiveCollection: 926000,
      strategies: [
        {
          id: '1',
          name: 'Standard 30-Day Follow-up',
          status: 'active',
          amountInCollection: 289000,
          lastRun: new Date('2024-01-19T10:30:00'),
          emailsSent: 45,
          impactedCustomers: 23,
        },
        {
          id: '2',
          name: 'High-Risk Intensive',
          status: 'active',
          amountInCollection: 185000,
          lastRun: new Date('2024-01-19T09:15:00'),
          emailsSent: 28,
          impactedCustomers: 12,
        },
        {
          id: '3',
          name: 'Post-Dispute Resolution',
          status: 'paused',
          amountInCollection: 0,
          pausedBy: 'Mike Chen',
          pausedDate: new Date('2024-01-10T09:15:00'),
          pausedReason: 'Strategy needs review after recent dispute resolution changes',
          impactedCustomers: 45,
        },
        {
          id: '4',
          name: 'Final Notice Escalation',
          status: 'active',
          amountInCollection: 452000,
          lastRun: new Date('2024-01-19T08:00:00'),
          emailsSent: 67,
          impactedCustomers: 34,
        },
      ],
    };

    // AI Insights
    const aiInsights: AIInsights = {
      confidence: 89,
      recommendations: [],
    };

    // Filter Options
    const filterOptions: FilterOption[] = [
      { id: 'user-1', name: 'Sarah Johnson', initials: 'SJ' },
      { id: 'user-2', name: 'Mike Chen', initials: 'MC' },
      { id: 'user-3', name: 'Emily Rodriguez', initials: 'ER' },
      { id: 'user-4', name: 'David Kim', initials: 'DK' },
    ];

    return {
      todaysPriorities,
      agingPortfolio,
      performanceSnapshot,
      riskAlerts,
      collectionStrategies,
      aiInsights,
      filterOptions,
    };
  }, [selectedFilter]);

  const refreshData = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  return {
    ...data,
    refreshData,
  };
}
